import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { api } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";
import { STATUS } from "@/lib/format";
import { PageHeader } from "@/components/Common";
import { OrderTable } from "@/components/OrderTable";
import { OrderFormDialog } from "@/components/OrderFormDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Orders() {
  const { companyId } = useData();
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [creating, setCreating] = useState(false);
  const type = params.get("service_type") || "all";
  const status = params.get("status") || "all";

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v || v === "all") next.delete(k); else next.set(k, v);
    setParams(next);
  };

  const load = useCallback(() => {
    api.get("/orders", { params: { company_id: companyId || undefined, service_type: type === "all" ? undefined : type, status: status === "all" ? undefined : status, q: params.get("q") || undefined } })
      .then((r) => setOrders(r.data)).catch(() => {});
  }, [companyId, type, status, params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setQ(params.get("q") || ""); }, [params]);

  return (
    <div data-testid="orders-page">
      <PageHeader eyebrow="Operations" title="Ordini e ticket">
        {can("orders:write") && <Button onClick={() => setCreating(true)} data-testid="new-order-button"><Plus className="mr-1 h-4 w-4" />Nuovo ordine</Button>}
      </PageHeader>
      <div className="mb-4 flex flex-wrap gap-3">
        <form className="relative min-w-[260px] flex-1" onSubmit={(e) => { e.preventDefault(); setParam("q", q); }}>
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Codice, cliente, località, indirizzo" className="bg-white pl-9" data-testid="orders-search-input" />
        </form>
        <Select value={type} onValueChange={(v) => setParam("service_type", v)}>
          <SelectTrigger className="w-40 bg-white" data-testid="orders-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutte le attività</SelectItem><SelectItem value="DELIVERY">Delivery</SelectItem><SelectItem value="ASSURANCE">Assurance</SelectItem><SelectItem value="CONSTRUCTION">Construction</SelectItem></SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => setParam("status", v)}>
          <SelectTrigger className="w-44 bg-white" data-testid="orders-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="mb-2 font-mono text-xs text-muted-foreground" data-testid="orders-count">{orders.length} ordini</div>
      <OrderTable orders={orders} />
      <OrderFormDialog open={creating} onOpenChange={setCreating} onSaved={load} />
    </div>
  );
}
